import React, { ChangeEvent, CSSProperties, useCallback, useMemo, useRef } from 'react';
import { ReactSVG } from 'react-svg';
import MaskedInput from 'react-text-mask';
import classNames from 'classnames';
import { FieldColorNames } from 'Hooks/DocumentFields/useDocumentFieldColor';
import { DatePipeOptions } from 'Interfaces/Common';
import { DocumentField } from 'Interfaces/DocumentFields';
import { DateFormats } from 'Interfaces/User';
import { dateFormatMasks } from 'Utils/formatters';
import { createAutoCorrectedDatePipe } from 'Utils/functions';

import DateIcon from 'Assets/images/icons/date-icon.svg';

interface DateFieldProps {
  onFieldDataChange?: (fieldData: Partial<DocumentField>) => void;
  value?: string | null;
  style?: CSSProperties;
  onFocus?: () => void;
  onBlur?: () => void;
  dateFormat?: NonNullable<DocumentField['dateFormat']>;
  disabled?: boolean;
  isInsertable?: boolean;
  fieldColor?: FieldColorNames;
  datePipeOptions?: DatePipeOptions;
}

export const DateField = ({
  value,
  onFieldDataChange,
  style,
  onFocus,
  onBlur,
  dateFormat,
  fieldColor,
  disabled,
  isInsertable,
  datePipeOptions,
}: DateFieldProps) => {
  const shadowRef = useRef<HTMLSpanElement>(null);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldDataChange?.({ text: event.target.value });
    },
    [onFieldDataChange],
  );

  const mask = useMemo(() => dateFormatMasks[dateFormat || DateFormats.MM_DD_YYYY], [
    dateFormat,
  ]);

  const datePipe = useMemo(
    () => createAutoCorrectedDatePipe(dateFormat, datePipeOptions),
    [dateFormat, datePipeOptions],
  );

  return (
    <div
      style={style}
      className={classNames('fieldDropDown__trigger-date', `fieldColor__${fieldColor}`, {
        'fieldDropDown__trigger--disabled': disabled,
        disabled_pointer_event: disabled || !isInsertable,
      })}
    >
      <span
        ref={shadowRef}
        className="fieldDropDown__trigger-date-content fieldDropDown__trigger-date-shadow"
      >
        {value || dateFormat}
      </span>
      <MaskedInput
        onFocus={onFocus}
        onBlur={onBlur}
        mask={mask}
        pipe={datePipe}
        type="text"
        className={classNames(
          'fieldDropDown__trigger-date-content',
          'fieldDropDown__trigger-date-input',
          {
            'fieldDropDown__trigger--disabled': disabled,
          },
        )}
        placeholder={dateFormat || 'Date'}
        defaultValue={value || ''}
        onChange={handleChange}
        disabled={disabled || !isInsertable}
      />
      <ReactSVG src={DateIcon} className="fieldDropDown__trigger-date-icon" />
    </div>
  );
};

export default DateField;
